//
//  Fleet.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 22, 2026
//  Author: Bob Keathley
//
//  Fleet overview page: machines and run summary. Re-exports ago() for the
//  Machines page and others.
//
import { useEffect, useState } from 'react'
import { api } from '@shared/api/client'
import { isOk, type Envelope } from '@shared/api/envelope'
import { templateRef, type FleetSummary } from '@shared/api/types'
import type { Session } from '@shared/auth'
import { href } from '@shared/router'
import { ago, EstSpend } from './format'

export { ago } // re-exported: Machines.tsx and others already import `ago` from here

export default function Fleet({ session }: { session: Session }) {
  const [r, setR] = useState<Envelope<FleetSummary> | null>(null)
  useEffect(() => {
    setR(null)
    api<FleetSummary>('/fleet/summary', {}, session.token).then(setR)
  }, [session.token])
  if (r === null) return <p className="hint">Loading fleet…</p>
  if (!isOk(r)) return <p className="err" role="alert">{r.error.code}: {r.error.message}</p>
  const { totals, notebooks, machines } = r.data
  const byMachine = Object.fromEntries(machines.map((m) => [m.id, m]))
  const tiles: [string, number, string][] = [
    ['Machines', totals.machines, ''], ['Notebooks', totals.notebooks, ''], ['Deeds', totals.deeds, ''],
    ['Denied', totals.deny, 'deny'], ['Watch', totals.watch, 'watch'], ['Diverged', totals.diverged, totals.diverged ? 'deny' : ''],
  ]
  return (
    <>
      <h2>Fleet</h2>
      <div className="tiles">
        {tiles.map(([l, n, c]) => <div key={l} className={`tile ${c}`}><div className="n">{n}</div><div className="l">{l}</div></div>)}
        <div className="tile"><div className="n">{totals.runs}</div><div className="l">Runs</div></div>
        <div className="tile"><div className="n">{totals.live_runs}</div><div className="l">Live</div></div>
        <div className="tile"><div className="n"><EstSpend usd={totals.usd_est} /></div><div className="l">Est. spend</div></div>
      </div>
      <h2>Notebooks</h2>
      <table>
        <thead><tr><th>Notebook</th><th>Template</th><th>User</th><th>Kind</th><th>Machine</th><th className="rt">Head seq</th><th className="rt">Denied</th><th className="rt">Watch</th><th>Last seen</th><th>Chain</th><th></th></tr></thead>
        <tbody>
          {notebooks.length === 0 && <tr><td colSpan={11} className="hint">No notebooks attested yet. Enrol a machine and run <code>signet birth</code>; it attests on first contact.</td></tr>}
          {notebooks.map((n) => (
            <tr key={n.id}>
              <td className="mono">{n.name}{n.title && <div className="hint" style={{ fontFamily: 'inherit' }}>{n.title}</div>}</td><td className="mono hint">{templateRef(n.template)}</td><td>{n.user ? <span title={n.user.email}>{n.user.display_name} <span className="hint">{n.user.email}</span></span> : <span className="hint">—</span>}</td><td>{n.kind}</td>
              <td className="mono">{n.machine_id ? (byMachine[n.machine_id]?.hostname ?? n.machine_id.slice(0, 8)) : <span className="hint">server</span>}</td>
              <td className="rt mono">{n.last_seq}</td>
              <td className="rt">{n.deny_count ? <span className="bad">{n.deny_count}</span> : 0}</td>
              <td className="rt">{n.watch_count ? <span style={{ color: 'var(--watch)' }}>{n.watch_count}</span> : 0}</td>
              <td className="hint">{ago(n.last_seen_at)}</td>
              <td>{n.divergence ? <span className="pill off" title={n.divergence}>diverged</span> : <span className="pill ok">intact</span>}</td>
              <td style={{ textAlign: 'right' }}><a href={href(`/notebooks/${n.id}/deeds`)}>deeds ›</a></td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}
